import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiUploadCloud } from "react-icons/fi";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { CASE_TYPES, CaseResult, CaseStatus, Kpi, MockBanner, RingKpi, SeverityChip, TypeChip, hasPerm } from "./qualityUi";
import useQualityMeta from "./useQualityMeta";
import TestCaseForm from "./TestCaseForm";
import TestCaseImportModal from "./TestCaseImportModal";

/**
 * Test Case Library — shared across sprints.
 *
 * Every regression run is built from here: Active cases flagged for regression
 * in the modules a release touches, plus every critical case. Drafts and
 * deprecated cases stay out of runs.
 */
export default function TestCaseLibrary() {
    const meta = useQualityMeta();
    const [all, setAll] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [f, setF] = useState({ module: "", case_type: "", status: "", q: "" });
    const [editing, setEditing] = useState(null); // {} for new, a case to edit
    const [importing, setImporting] = useState(false);
    // Row delete: `confirming` asks first; `blocked` holds the reason a case
    // that is release history can't be deleted, with Deprecate offered instead.
    const [confirming, setConfirming] = useState(null);
    const [blocked, setBlocked] = useState({});
    const [busyId, setBusyId] = useState(null);
    const canManage = hasPerm("Manage Test Cases");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [filtered, everything] = await Promise.all([qualityApi.getTestCases(f), qualityApi.getTestCases({})]);
            setRows(filtered.data || []);
            setAll(everything.data || []);
            setErr("");
        } catch (e) {
            setErr(e.message || "Could not load test cases.");
        } finally {
            setLoading(false);
        }
    }, [f]);

    useEffect(() => {
        const t = setTimeout(load, f.q ? 250 : 0);
        return () => clearTimeout(t);
    }, [load, f.q]);

    const k = useMemo(() => {
        const n = all.length || 1;
        const active = all.filter((c) => c.status === "Active");
        const reg = active.filter((c) => c.in_regression).length;
        return {
            total: all.length, regPct: (reg / n) * 100,
            critical: active.filter((c) => c.critical).length,
            edge: all.filter((c) => c.case_type !== "Positive").length,
            drafts: all.filter((c) => c.status === "Draft").length,
            never: all.filter((c) => !c.last_result && (!c.result || c.result === "Not Run")).length,
            n,
        };
    }, [all]);

    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
    // Suggested types plus any the library already uses (imports bring their own).
    const typeOptions = Array.from(new Set([...CASE_TYPES, ...all.map((c) => c.case_type).filter(Boolean)]));
    const filterCls = (v) => `qa-filter${v ? " qa-filter--on" : ""}`;

    const removeCase = async (c) => {
        setBusyId(c.id);
        try {
            const res = await qualityApi.deleteTestCase(c.id);
            showSuccessToast(res.message || `${c.code} deleted.`);
            setConfirming(null);
            await load();
        } catch (e) {
            setConfirming(null);
            if (e.payload?.code === "in_use") setBlocked((b) => ({ ...b, [c.id]: e.message }));
            else showErrorToast(e.message || "Could not delete the test case.");
        } finally {
            setBusyId(null);
        }
    };

    const deprecateCase = async (c) => {
        setBusyId(c.id);
        try {
            await qualityApi.updateTestCase(c.id, { status: "Deprecated" });
            showSuccessToast(`${c.code} deprecated. It stays in history and is left out of future runs.`);
            setBlocked((b) => { const n = { ...b }; delete n[c.id]; return n; });
            await load();
        } catch (e) {
            showErrorToast(e.message || "Could not deprecate the test case.");
        } finally {
            setBusyId(null);
        }
    };

    const openByCode = (code) => {
        const c = all.find((x) => x.code === code);
        if (c) setEditing(c);
    };

    return (
        <div className="qa-page">
            <MockBanner />
            <div className="qa-head">
                <h1>Test Case Library</h1>
                <span className="qa-hint">Shared across sprints. Only <b>Active</b> cases are picked for regression runs.</span>
                <span className="qa-sp" />
                {hasPerm("Manage Test Cases") && (
                    <>
                        <button className="qa-btn" onClick={() => setImporting(true)}>
                            <FiUploadCloud size={12} /> Import from Excel
                        </button>
                        <button className="qa-btn qa-btn--primary" onClick={() => setEditing({})}>
                            <FiPlus size={12} /> New test case
                        </button>
                    </>
                )}
            </div>

            <div className="qa-kpis">
                <RingKpi percent={k.regPct} value={k.total} label="Test cases (in regression %)" />
                <Kpi value={k.critical} label="Critical" color="var(--qa-red)" fill={(k.critical / k.n) * 100} />
                <Kpi value={k.edge} label="Negative / boundary" color="var(--qa-purple)" fill={(k.edge / k.n) * 100} />
                <Kpi value={k.drafts} label="Drafts" color="var(--qa-amber)" fill={(k.drafts / k.n) * 100} />
                <Kpi value={k.never} label="Never run" color="var(--qa-orange)" fill={(k.never / k.n) * 100} />
            </div>

            <div className="qa-toolbar">
                <span className="qa-toolbar__count">{loading ? "Loading…" : `${rows.length} test case${rows.length === 1 ? "" : "s"}`}</span>
                <select id="qa-tc-f-module" className={filterCls(f.module)} value={f.module} onChange={(e) => set({ module: e.target.value })} aria-label="Module">
                    <option value="">Module</option>
                    {meta.modules.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                </select>
                <select id="qa-tc-f-type" className={filterCls(f.case_type)} value={f.case_type} onChange={(e) => set({ case_type: e.target.value })} aria-label="Type">
                    <option value="">Type</option>
                    {typeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select id="qa-tc-f-status" className={filterCls(f.status)} value={f.status} onChange={(e) => set({ status: e.target.value })} aria-label="Status">
                    <option value="">Status</option>
                    <option value="Active">Active</option>
                    <option value="Draft">Draft</option>
                    <option value="Deprecated">Deprecated</option>
                </select>
                <input
                    id="qa-tc-f-q" className="qa-search" placeholder="Search scenario, description, screen…" value={f.q}
                    onChange={(e) => set({ q: e.target.value })} aria-label="Search test cases"
                />
            </div>

            <div className="qa-card">
                {err ? <div className="qa-empty">{err}</div> : (
                    <table className="qa-tbl">
                        <thead>
                            <tr>
                                <th>S.No</th><th>Test case</th><th>Module</th><th>Sub Module</th><th>Screen</th>
                                <th>Test Case Scenario</th><th>Test Case Description</th><th>Test Type</th><th>Severity</th>
                                <th>Result</th><th>Status</th>
                                {canManage && <th aria-label="Actions" />}
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={canManage ? 12 : 11} className="qa-empty">No test cases match these filters.</td></tr>
                            )}
                            {rows.map((c, i) => {
                                return (
                                    <tr key={c.id} className="qa-click" onClick={() => setEditing(c)}>
                                        <td className="qa-num qa-muted">{i + 1}</td>
                                        <td className="qa-code qa-mono">{c.code}</td>
                                        <td className="qa-nw">{c.module}</td>
                                        <td className="qa-nw">{c.sub_module || <span className="qa-faint">—</span>}</td>
                                        <td className="qa-nw">{c.screen || <span className="qa-faint">—</span>}</td>
                                        <td className="qa-wrap">
                                            {c.scenario || <span className="qa-faint">—</span>}
                                            {c.applies_to === "setting" && <div className="qa-hint">Only when: {c.setting_name}</div>}
                                        </td>
                                        <td className="qa-wrap"><div className="qa-clamp">{c.description || c.title}</div></td>
                                        <td><TypeChip value={c.case_type} /></td>
                                        <td><SeverityChip value={c.severity} /></td>
                                        <td className="qa-nw"><CaseResult testCase={c} /></td>
                                        <td><CaseStatus value={c.status} /></td>
                                        {canManage && (
                                            // The row opens the editor, so clicks in here must not bubble.
                                            <td className="qa-nw" onClick={(e) => e.stopPropagation()}>
                                                {blocked[c.id] ? (
                                                    <div className="qa-rowblock">
                                                        <span className="qa-hint" title={blocked[c.id]}>Can't delete: used in a release.</span>
                                                        {c.status !== "Deprecated" && (
                                                            <button className="qa-btn qa-btn--sm" disabled={busyId === c.id} onClick={() => deprecateCase(c)}>
                                                                Mark Deprecated
                                                            </button>
                                                        )}
                                                        <button className="qa-x" aria-label="Dismiss"
                                                            onClick={() => setBlocked((b) => { const n = { ...b }; delete n[c.id]; return n; })}>×</button>
                                                    </div>
                                                ) : confirming === c.id ? (
                                                    <div className="qa-rowblock">
                                                        <span className="qa-hint">Delete {c.code}?</span>
                                                        <button className="qa-btn qa-btn--sm qa-btn--danger" disabled={busyId === c.id} onClick={() => removeCase(c)}>
                                                            {busyId === c.id ? "Deleting…" : "Delete"}
                                                        </button>
                                                        <button className="qa-btn qa-btn--sm" disabled={busyId === c.id} onClick={() => setConfirming(null)}>Keep</button>
                                                    </div>
                                                ) : (
                                                    <button className="qa-btn qa-btn--sm qa-btn--danger" onClick={() => setConfirming(c.id)}>
                                                        Delete
                                                    </button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {importing && (
                <TestCaseImportModal
                    onClose={() => setImporting(false)}
                    onImported={() => { setImporting(false); load(); }}
                />
            )}

            {editing && (
                <TestCaseForm
                    key={editing.id || "new"}
                    testCase={editing.id ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                    onDeleted={() => { setEditing(null); load(); }}
                    onOpenOther={(s) => openByCode(s.code)}
                />
            )}
        </div>
    );
}
